import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  EllipsisSpan,
  Form,
  FormTitle,
  Input,
  Modal,
  PopConfirm,
  SubContent,
  Table,
  useBreadcrumbRoutes,
  Wrapper,
} from '@maxtropy/components';
import { useNavigate, useParams } from 'react-router-dom';
import { Cascader, Col, Row, Space } from 'antd';
import {
  COMPONENT_MODE,
  COMPONENT_MODE_LABELS,
  ComponentMode,
  FIELD_TYPE_OPTIONS,
  FIELDS,
} from '@/pages/AssetInformationManagement/constant';
import { ModalForm } from '@/pages/AssetInformationManagement/CreateOrEditTemplate/ModalForm';
import { useDeviceTypeCascaderOptions } from '@/pages/AssetInformationManagement/hooks';
import { PlusOutlined } from '@ant-design/icons';
import {
  apiV2AssetTemplateAddPost,
  apiV2AssetTemplateDetailPost,
  apiV2AssetTemplateUpdatePost,
} from '@maxtropy/device-customer-apis-v2';
import { buildCascaderPathById } from '@/pages/AssetInformationManagement/utils';
import { v4 as uuid } from 'uuid';

interface Props {
  mode: ComponentMode;
}

const columns = [
  {
    title: '字段名称',
    dataIndex: FIELDS.FIELD_NAME,
    width: 180,
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '字段类型',
    dataIndex: FIELDS.FIELD_TYPE,
    width: 180,
    ellipsis: { showTitle: true },
    render: (v: number) => <EllipsisSpan value={FIELD_TYPE_OPTIONS.find(i => i.value === v)?.label || '-'} />,
  },
];

const CreateOrEditTemplate: React.FC<Props> = ({ mode }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [loading, setLoading] = useState<boolean>(false);
  const [fieldDataList, setFieldDataList] = useState<any[]>([]);
  const [visibleModal, setVisibleModal] = useState<boolean>(false);
  // 最开始没给id, 只能根据索引来; table不拖拽,问题不大
  const [edit, setEdit] = useState<{ index: number; mode: ComponentMode; value: object }>({
    index: 0,
    mode: COMPONENT_MODE.CREATE,
    value: {},
  });

  const routesTitle = useMemo(() => ({ name: `${COMPONENT_MODE_LABELS[mode]}模版` }), [mode]);

  const cascaderOptions = useDeviceTypeCascaderOptions();

  const getFormInitialValues = useCallback(() => {
    return {
      [FIELDS.TEMPLATE_NAME]: '',
      [FIELDS.DEVICE_TYPE]: [],
    };
  }, []);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await apiV2AssetTemplateDetailPost({ id });
      // 手动给enumlist加id
      // 后端返回string[], 在fetch和submit的时候改成string[], 中途用object[]
      resp?.[FIELDS.FIELD_DATA_LIST]?.forEach(item => {
        const enumList = item[FIELDS.ENUM_ITEM_LIST];
        if (enumList && enumList.length) {
          const arr = enumList.map((name, index) => {
            return {
              // id: crypto.randomUUID(),
              id: uuid(),
              name,
            };
          });
          (item as any)[FIELDS.ENUM_ITEM_LIST] = arr;
        }
      });
      console.log('/detail的resp', resp);
      form.setFieldsValue({
        [FIELDS.TEMPLATE_NAME]: resp[FIELDS.TEMPLATE_NAME],
        [FIELDS.DEVICE_TYPE]: buildCascaderPathById(cascaderOptions, resp[FIELDS.DEVICE_TYPE] ?? ''),
      });
      setFieldDataList(resp[FIELDS.FIELD_DATA_LIST] || []);
    } finally {
      setLoading(false);
    }
  }, [id, form, cascaderOptions]);

  // 数据初始化
  useEffect(() => {
    if (mode === COMPONENT_MODE.VIEW || mode === COMPONENT_MODE.EDIT) {
      fetchDetail();
    }
  }, [fetchDetail, mode]);

  const lastColumns = [
    ...columns,
    {
      title: '操作',
      dataIndex: 'operation',
      width: 120,
      fixed: 'right' as 'right',
      render: (_: any, record: any, index: number) => {
        return (
          <Space size={16}>
            <Button
              type="link"
              onClick={() => {
                setVisibleModal(true);
                setEdit({ index, mode: COMPONENT_MODE.VIEW, value: { ...record } });
              }}
            >
              查看
            </Button>

            <Button type="link" onClick={() => handleRowEdit(record, index)}>
              编辑
            </Button>

            <PopConfirm
              placement="top"
              title="确认删除？删除后不可恢复，你还要继续吗？"
              onConfirm={() => handleRowDelete(index)}
            >
              <Button type="link">删除</Button>
            </PopConfirm>
          </Space>
        );
      },
    },
  ];

  // 设备类型搜索函数
  const deviceTypeFilter = (inputValue: string, path: any[]) =>
    path.some(option => option.label.toLowerCase().includes(inputValue.toLowerCase()));

  // 弹窗确认后的回调
  const onModalFinish = (values: any) => {
    console.log('onModalFinish');
    const newData = [...fieldDataList];
    if (edit.mode === COMPONENT_MODE.EDIT) {
      newData.splice(edit.index, 1, values);
    } else if (edit.mode === COMPONENT_MODE.CREATE) {
      newData.push(values);
    }
    setFieldDataList(newData);
  };

  const cancel = useCallback(() => {
    form.resetFields();
    setFieldDataList([]);
    // setLoading(false);
    navigate(`/asset/equipment/assetInfo`);
  }, [form, navigate]);

  // 四级页面的提交
  const submit = useCallback(() => {
    if (loading) return;
    setLoading(true);
    // 对enumList处理
    fieldDataList.forEach(item => {
      const enumList = item[FIELDS.ENUM_ITEM_LIST];
      if (enumList && enumList.length) {
        item[FIELDS.ENUM_ITEM_LIST] = enumList.map((item: any) => item.name);
      }
    });
    const typeIds = form.getFieldValue(FIELDS.DEVICE_TYPE);
    const templateName = form.getFieldValue(FIELDS.TEMPLATE_NAME);
    const params = {
      id,
      templateName,
      typeId: typeIds?.at(-1),
      fieldDataList,
    };
    const api = mode === COMPONENT_MODE.CREATE ? apiV2AssetTemplateAddPost : apiV2AssetTemplateUpdatePost;
    api(params)
      .then(resp => {
        cancel();
        console.log('接口结果', resp);
      })
      .finally(() => setLoading(false));
  }, [loading, fieldDataList, form, id, mode, cancel]);

  // 字段配置中的表格的删除
  const handleRowDelete = (index: number) => {
    const arr = fieldDataList.filter((_, i) => i !== index);
    setFieldDataList(arr);
  };

  // 字段配置中的表格的编辑
  const handleRowEdit = (record: any, i: number) => {
    setEdit({ index: i, mode: COMPONENT_MODE.EDIT, value: { ...record } });
    setVisibleModal(true);
  };

  return (
    <Wrapper
      routes={[...(breadcrumbRoutes?.routes ?? []), routesTitle]}
      // className={styles.wrapper}
      isLoading={loading}
    >
      <FormTitle title={routesTitle.name} />

      <SubContent className={'mb-8'}>
        <Form
          initialValues={getFormInitialValues()}
          form={form}
          layout="horizontal"
          onFinish={submit}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 8 }}
        >
          <Form.Item
            label="模版名称"
            // layout="horizontal"
            name={FIELDS.TEMPLATE_NAME}
            rules={[
              { required: true, message: '请输入模版名称' },
              { max: 50, message: '最多输入50个字' },
            ]}
          >
            <Input placeholder="请输入模版名称" />
          </Form.Item>

          <Form.Item label="设备类型" name={FIELDS.DEVICE_TYPE} rules={[{ required: true, message: '请选择设备类型' }]}>
            <Cascader
              options={cascaderOptions}
              allowClear
              maxTagCount="responsive"
              showCheckedStrategy="SHOW_CHILD"
              showSearch={{ filter: deviceTypeFilter }}
              placeholder={'请选择设备类型'}
              disabled={mode === COMPONENT_MODE.EDIT}
            />
          </Form.Item>
        </Form>
      </SubContent>

      <SubContent title={'字段配置'} className={'mb-8'}>
        <Row>
          <Col span={12}>
            <Button
              type="primary"
              wrapClassName={'mb-8'}
              icon={<PlusOutlined onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />}
              onClick={() => {
                setEdit({ mode: COMPONENT_MODE.CREATE, index: 0, value: {} });
                setVisibleModal(true);
              }}
            >
              新增字段
            </Button>
            <Table
              columns={lastColumns}
              // scroll={{ y: 400 }}
              dataSource={fieldDataList}
              // rowKey="id"
            />
          </Col>
        </Row>

        <Space
          size={8}
          style={{ marginTop: 24 }}
          // className="sticky-footer"
        >
          <Button
            type="primary"
            onClick={() => {
              form.submit();
            }}
          >
            保存
          </Button>
          <Button
            onClick={() => {
              Modal.confirm({
                title: '是否放弃所有未保存信息并返回列表？',
                onOk: cancel,
              });
            }}
          >
            取消
          </Button>
        </Space>
      </SubContent>

      {/* 弹窗 */}
      <ModalForm
        mode={edit.mode}
        fieldNames={fieldDataList.map(item => item[FIELDS.FIELD_NAME])}
        visible={visibleModal}
        setVisible={setVisibleModal}
        onFinish={onModalFinish}
        echoValue={edit.value}
      />
    </Wrapper>
  );
};

export default CreateOrEditTemplate;
